import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Plus, Search, Grid, List, TrendingUp, Users, FileText, CheckCircle, Building2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { teamAPI } from '../services/api';
import api from '../services/api';
import { Publication, Team } from '../types';
import PublicationCard from '../components/Publications/PublicationCard';
import PublicationListItem from '../components/Publications/PublicationListItem';
import NewPublicationModal from '../components/Publications/NewPublicationModal';
import logoImage from '../images/image.png';
import toast from 'react-hot-toast';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [publications, setPublications] = useState<Publication[]>([]);
  const [filteredPublications, setFilteredPublications] = useState<Publication[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStage, setSelectedStage] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<number | null>(null);
  const [isTeamFilterActive, setIsTeamFilterActive] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Pagination: 36 for grid view, 20 for list view
  const itemsPerPage = viewMode === 'grid' ? 36 : 20;

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    inProgress: 0,
    published: 0,
    stage1: 0,
    stage2: 0,
    stage3: 0,
    stage4: 0,
    users: 0,
  });

  const loadPublications = useCallback(async () => {
    try {
      setIsLoading(true);
      // Use batch endpoint to reduce API calls
      const response = await api.get('/dashboard/batch');
      const { publications, stats, teams } = response.data;
      
      setPublications(publications);
      setTeams(teams);
      
      // Update stats from batch response
      setStats({
        total: stats.total_papers,
        inProgress: stats.in_progress_papers,
        published: stats.published_papers,
        stage1: publications.filter((p: Publication) => p.stage === '1').length,
        stage2: publications.filter((p: Publication) => p.stage === '2').length,
        stage3: publications.filter((p: Publication) => p.stage === '3').length,
        stage4: publications.filter((p: Publication) => p.stage === '4').length,
        users: stats.active_users,
      });
    } catch (error: any) {
      toast.error('Failed to load publications');
      console.error('Error loading publications:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Removed - now handled in batch endpoint

  // Removed - now handled in batch endpoint

  const handleTeamFilter = async (teamId: number) => {
    try {
      setIsLoading(true);
      const teamPublications = await teamAPI.getTeamPublications(teamId);
      setFilteredPublications(teamPublications);
      setSelectedTeam(teamId);
      setIsTeamFilterActive(true);
    } catch (error: any) {
      console.error('Error loading team publications:', error);
      toast.error('Failed to load team publications');
    } finally {
      setIsLoading(false);
    }
  };

  const clearTeamFilter = () => {
    setSelectedTeam(null);
    setIsTeamFilterActive(false);
    filterPublications();
  };

  const filterPublications = useCallback(() => {
    let filtered = publications;

    // Filter by stage
    if (selectedStage !== 'all') {
      if (selectedStage === 'published') {
        filtered = filtered.filter(p => p.stage === 'published');
      } else {
        filtered = filtered.filter(p => p.stage === selectedStage);
      }
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(p => {
        // Search in title
        if (p.title.toLowerCase().includes(searchLower)) return true;
        
        // Search in abstract
        if (p.abstract.toLowerCase().includes(searchLower)) return true;
        
        // Search in lead author name
        const leadAuthorName = (p.leadAuthorName || p.lead_author_name || '').toLowerCase();
        if (leadAuthorName.includes(searchLower)) return true;
        
        // Search in co-author names
        if (p.authors && p.authors.length > 0) {
          for (const author of p.authors) {
            const authorFullName = `${author.firstName || ''} ${author.lastName || ''}`.toLowerCase().trim();
            const authorFirstName = (author.firstName || '').toLowerCase();
            const authorLastName = (author.lastName || '').toLowerCase();
            
            if (authorFullName.includes(searchLower) || 
                authorFirstName.includes(searchLower) || 
                authorLastName.includes(searchLower)) {
              return true;
            }
          }
        }
        
        return false;
      });
    }

    setFilteredPublications(filtered);
  }, [publications, selectedStage, searchTerm]);

  useEffect(() => {
    loadPublications(); // This now loads everything in batch
  }, [loadPublications]);

  // Refresh data when the page becomes visible (e.g., when navigating back from detail page)
  useEffect(() => {
    const handleFocus = () => {
      loadPublications(); // This now loads everything in batch
    };

    // Listen for custom refresh event (e.g., from PublicationCard after team update)
    const handleRefresh = () => {
      loadPublications();
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('refreshPublications', handleRefresh);
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('refreshPublications', handleRefresh);
    };
  }, [loadPublications]);

  useEffect(() => {
    filterPublications();
    setCurrentPage(1); // Reset to first page when filters change
  }, [publications, searchTerm, selectedStage, filterPublications]);
  
  useEffect(() => {
    setCurrentPage(1); // Reset to first page when view mode changes
  }, [viewMode]);
  
  // Get paginated publications
  const paginatedPublications = filteredPublications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(filteredPublications.length / itemsPerPage);

  // Auto-open New Publication modal when ?new=1 is present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new') === '1') {
      setIsModalOpen(true);
      // Clean the URL so refresh won't re-open repeatedly
      params.delete('new');
      navigate({ pathname: '/publications', search: params.toString() }, { replace: true });
    }
  }, [location.search, navigate]);

  const handleModalSuccess = async () => {
    // Clear cache to ensure fresh data
    const { cacheService } = await import('../services/cache');
    cacheService.clearPattern('/dashboard/batch');
    cacheService.clearPattern('/admin/batch');
    loadPublications();
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'all': return 'bg-gray-100 text-gray-800 border-gray-200';
      case '1': return 'bg-blue-100 text-blue-800 border-blue-200';
      case '2': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case '3': return 'bg-purple-100 text-purple-800 border-purple-200';
      case '4': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'published': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <img 
            src={logoImage} 
            alt="Publication Tracker Logo" 
            className="h-16 w-auto mx-auto mb-4"
          />
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading publications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Research Publication Pipeline</h1>
            <p className="text-xl text-green-100 mb-8">
              Streamline collaboration and track your research from idea to publication
            </p>
            
            {/* Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              <Link to="/dashboard/total-papers" className="block">
                <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 hover:bg-opacity-30 transition-all duration-200 cursor-pointer transform hover:scale-105">
                  <FileText className="h-8 w-8 mx-auto mb-2" />
                  <div className="text-2xl font-bold">{stats.total}</div>
                  <div className="text-sm text-green-100">Total Papers</div>
                </div>
              </Link>
              <Link to="/dashboard/in-progress" className="block">
                <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 hover:bg-opacity-30 transition-all duration-200 cursor-pointer transform hover:scale-105">
                  <TrendingUp className="h-8 w-8 mx-auto mb-2" />
                  <div className="text-2xl font-bold">{stats.inProgress}</div>
                  <div className="text-sm text-green-100">In Progress</div>
                </div>
              </Link>
              <Link to="/dashboard/published" className="block">
                <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 hover:bg-opacity-30 transition-all duration-200 cursor-pointer transform hover:scale-105">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2" />
                  <div className="text-2xl font-bold">{stats.published}</div>
                  <div className="text-sm text-green-100">Published</div>
                </div>
              </Link>
              <Link to="/dashboard/active-users" className="block">
                <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 hover:bg-opacity-30 transition-all duration-200 cursor-pointer transform hover:scale-105">
                  <Users className="h-8 w-8 mx-auto mb-2" />
                  <div className="text-2xl font-bold">{stats.users || 0}</div>
                  <div className="text-sm text-green-100">Active Users</div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Pipeline Overview with Integrated Controls */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-6 items-start justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <TrendingUp className="h-6 w-6 mr-2 text-green-600" />
              Publication Pipeline
            </h2>
            
            {/* Integrated Search and View Controls */}
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by title, abstract, or author..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              {/* Team Papers Filter */}
              {teams.length > 0 && (
                <div className="flex items-center space-x-2">
                  <Building2 className="h-4 w-4 text-gray-500" />
                  <select
                    value={selectedTeam || ''}
                    onChange={(e) => {
                      const teamId = e.target.value ? Number(e.target.value) : null;
                      if (teamId) {
                        handleTeamFilter(teamId);
                      } else {
                        clearTeamFilter();
                      }
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">All Teams</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.full_name}
                      </option>
                    ))}
                  </select>
                  {isTeamFilterActive && (
                    <button
                      onClick={clearTeamFilter}
                      className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                    >
                      Clear Filter
                    </button>
                  )}
                </div>
              )}

              {/* View Toggle */}
              <div className="flex border border-gray-300 rounded-lg">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-green-600 text-white' : 'text-gray-600'}`}
                  title="Grid View"
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-green-600 text-white' : 'text-gray-600'}`}
                  title="Table View"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>

            </div>
          </div>
          
          {/* Pipeline Stages */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {[
              { stage: 'all', title: 'All', description: 'View all publications across all stages' },
              { stage: '1', title: 'Ideation', description: 'Submit analysis plan with abstract and methods' },
              { stage: '2', title: 'Review & Team', description: 'Committee approval and co-author recruitment' },
              { stage: '3', title: 'Data Analysis', description: 'Perform analysis with Data Team support' },
              { stage: '4', title: 'Manuscript', description: 'Draft and refine for submission' },
            ].map((item, index) => (
              <div 
                key={item.stage} 
                className="text-center cursor-pointer hover:bg-gray-50 rounded-lg p-4 transition-colors duration-200"
                onClick={() => setSelectedStage(item.stage)}
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full text-white font-bold text-lg mb-3 transition-transform duration-200 hover:scale-110 ${
                  item.stage === 'all' ? 'bg-gray-600' :
                  item.stage === '1' ? 'bg-blue-600' :
                  item.stage === '2' ? 'bg-yellow-600' :
                  item.stage === '3' ? 'bg-purple-600' :
                  'bg-indigo-600'
                }`}>
                  {item.stage === 'all' ? 'All' : item.stage}
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border transition-colors duration-200 ${
                  selectedStage === item.stage 
                    ? 'bg-green-100 text-green-800 border-green-300' 
                    : getStageColor(item.stage)
                }`}>
                  {item.stage === 'all' ? stats.total : (stats as any)[`stage${item.stage}`]} papers
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Publications Grid */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <FileText className="h-6 w-6 mr-2 text-green-600" />
              Active Publications ({filteredPublications.length})
            </h2>
            
            {user && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>New Publication</span>
              </button>
            )}
          </div>

          {filteredPublications.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No publications found</h3>
              <p className="text-gray-600">
                {searchTerm || selectedStage !== 'all'
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Get started by submitting your first publication idea.'}
              </p>
            </div>
          ) : (
            <>
              <div className={`${
                viewMode === 'grid'
                  ? 'grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                  : 'space-y-2'
              }`}>
                {paginatedPublications.map((publication) => (
                  viewMode === 'grid' ? (
                    <PublicationCard key={publication.id} publication={publication} />
                  ) : (
                    <PublicationListItem key={publication.id} publication={publication} />
                  )
                ))}
              </div>
              
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-700">
                    Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredPublications.length)} of {filteredPublications.length} items
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-1 text-sm border rounded-md ${
                              currentPage === pageNum
                                ? 'bg-green-600 text-white border-green-600'
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* New Publication Modal */}
      <NewPublicationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default Dashboard;
